import React, { useState, useEffect } from 'react';
import { X, Loader2, Send, Copy, CheckCircle, RefreshCw, Megaphone } from 'lucide-react';
import toast from 'react-hot-toast';

export interface AdDraft {
  draft_id: string | null;
  headline: string;
  subheadline: string;
  body: string;
  cta: string;
  audience: string;
  message_type: string;
}

interface AdPreviewModalProps {
  draft: AdDraft;
  propertyAddress: string;
  onClose: () => void;
  onRegenerate: () => void;
  onSendToMake: (adData: AdDraft) => void;
  isRegenerating: boolean;
  isSending: boolean;
}

export default function AdPreviewModal({
  draft,
  propertyAddress,
  onClose,
  onRegenerate,
  onSendToMake,
  isRegenerating,
  isSending,
}: AdPreviewModalProps) {
  const [headline, setHeadline] = useState(draft.headline);
  const [subheadline, setSubheadline] = useState(draft.subheadline);
  const [body, setBody] = useState(draft.body);
  const [cta, setCta] = useState(draft.cta);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setHeadline(draft.headline);
    setSubheadline(draft.subheadline);
    setBody(draft.body);
    setCta(draft.cta);
  }, [draft]);

  const handleCopy = async () => {
    const text = `${headline}\n${subheadline}\n\n${body}\n\n${cta}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('הועתק ללוח');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = () => {
    onSendToMake({
      ...draft,
      headline,
      subheadline,
      body,
      cta,
    });
  };

  const audienceLabel = draft.audience === 'buyer' ? 'קונה' : 'מוכר';
  const typeLabels: Record<string, string> = {
    info: 'מודעת מידע',
    opportunity: 'הזדמנות',
    emotion: 'רגש / לייף סטייל',
    proof: 'הוכחת יכולת',
    valuation: 'הערכת שווי',
    pain: 'נקודת כאב',
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60" dir="rtl">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-[640px] mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Megaphone size={18} className="text-blue-400" />
              תצוגת מודעה
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {propertyAddress} | {audienceLabel} — {typeLabels[draft.message_type] || draft.message_type}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Headline */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">כותרת ראשית:</label>
            <input
              type="text"
              value={headline}
              onChange={e => setHeadline(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white font-bold focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Subheadline */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">כותרת משנה:</label>
            <input
              type="text"
              value={subheadline}
              onChange={e => setSubheadline(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">תוכן מודעה:</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={5}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 resize-y leading-relaxed"
            />
          </div>

          {/* CTA */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">קריאה לפעולה (CTA):</label>
            <input
              type="text"
              value={cta}
              onChange={e => setCta(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700">
          <p className="text-[11px] text-gray-500">טיוטה — ניתן לערוך לפני שליחה</p>
          <div className="flex items-center gap-2">
            <button
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={13} className={isRegenerating ? 'animate-spin' : ''} />
              צור מחדש
            </button>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            >
              {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
              {copied ? 'הועתק' : 'העתק'}
            </button>
            <button
              onClick={handleSend}
              disabled={isSending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {isSending ? 'שולח...' : 'שלח (Make)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdGeneratingModal({ propertyAddress }: { propertyAddress: string }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60" dir="rtl">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl p-8 text-center max-w-sm mx-4">
        <Loader2 size={32} className="animate-spin text-blue-400 mx-auto mb-3" />
        <p className="text-white font-medium">יוצר מודעה...</p>
        <p className="text-xs text-gray-400 mt-1">{propertyAddress}</p>
      </div>
    </div>
  );
}
